import React, { useState, useRef, useEffect } from "react";
import { stateData, panelTypes, shadingOpts, dirOpts, PANEL_AREA_SQFT } from "../shared/Data";
import { round, parseNumber } from "../shared/helpers";
import "../style.css";

const DEFAULTS = {
  basePerformanceRatio: 0.78,
  panelMarginFactor: 0.92,
  installCostPerWatt: 2.95,
};

export default function SystemCalculator() {
  const [selectedState, setSelectedState] = useState("");
  const [monthlyBill, setMonthlyBill] = useState("");
  const [tariff, setTariff] = useState("");
  const [roofArea, setRoofArea] = useState("");
  const [roofDir, setRoofDir] = useState("south");
  const [shading, setShading] = useState("none");
  const [panelType, setPanelType] = useState("mono");
  const [moduleWatt, setModuleWatt] = useState(400);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const handleCalculate = () => {
    setError("");
    const state = selectedState && stateData[selectedState] ? stateData[selectedState] : stateData["Texas"];
    const normalizedTariff = parseNumber(tariff, state.utility);
    const bill = parseNumber(monthlyBill);
    const usageValid = bill > 0;
    const roofAreaValue = parseNumber(roofArea);
    const moduleWattValue = parseNumber(moduleWatt);

    if (!usageValid) {
      setError("Enter a valid monthly electricity bill to calculate system size.");
      return;
    }
    if (roofAreaValue <= 0) {
      setError("Enter usable roof area to estimate how many panels will fit.");
      return;
    }
    if (!Number.isFinite(moduleWattValue) || moduleWattValue < 250 || moduleWattValue > 800) {
      setError("Module wattage must be a number between 250 and 800.");
      return;
    }
    if (roofAreaValue < PANEL_AREA_SQFT) {
      setError(`Roof area must be at least ${PANEL_AREA_SQFT} sq ft to fit one panel.`);
      return;
    }
    const annualUsage = normalizedTariff > 0 ? (bill / normalizedTariff) * 12 : 0;
    const annualProductionPerKw = state.sun * 12 * DEFAULTS.basePerformanceRatio * (shading === "none" ? 1.0 : shading === "light" ? 0.92 : shading === "moderate" ? 0.80 : 0.65) * (roofDir === "south" ? 1.0 : roofDir === "east" || roofDir === "west" ? 0.88 : 0.70);
    const requiredKw = annualProductionPerKw > 0 ? annualUsage / annualProductionPerKw : 0;
    const panel = panelTypes.find(p => p.id === panelType) || panelTypes[0];
    const panelWatt = Number.isFinite(moduleWattValue) && moduleWattValue > 0 ? moduleWattValue : (Number.isFinite(panel.defaultWatt) ? panel.defaultWatt : 0);
    const requiredPanels = panelWatt && Number.isFinite(requiredKw) ? Math.ceil((requiredKw * 1000) / panelWatt) : 0;
    const maxPanels = Math.floor(roofAreaValue / PANEL_AREA_SQFT);
    const installedPanels = Number.isFinite(requiredPanels) ? Math.min(requiredPanels, maxPanels) : 0;
    const installedKw = round((installedPanels * panelWatt) / 1000, 2);
    const systemCost = round(installedKw * 1000 * DEFAULTS.installCostPerWatt, 0);
    const estimatedProduction = round(installedKw * state.sun * 12 * 0.78 * 0.96 * 0.97, 0);
    const savings = round(Math.min(estimatedProduction, annualUsage) * normalizedTariff, 0);
    const paybackYears = savings > 0 ? round(systemCost / savings, 1) : null;

    setResult({
      state: selectedState || "National average",
      annualUsage: round(annualUsage, 0),
      requiredKw: round(requiredKw, 2),
      moduleWatt: round(panelWatt, 0),
      requiredPanels,
      maxPanels,
      installedPanels,
      installedKw,
      systemCost,
      estimatedProduction,
      savings,
      paybackYears,
      roofWarning: requiredPanels > maxPanels,
    });
  };

  return (
    <div className="se-wrap">
      {/* <div className="se-hero">
        <div className="se-hero-badge">System Calculator</div>
        <h1 className="se-hero-h1">Residential solar system sizing</h1>
        <p className="se-hero-p">Estimate panel count, roof capacity, and system cost for a USA home.</p>
      </div> */}


      <div className="se-card">
        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">State</label>
            <select className="se-select" value={selectedState} onChange={e => setSelectedState(e.target.value)}>
              <option value="">National average</option>
              {Object.keys(stateData).sort().map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <div className="se-field">
            <label className="se-label">Average monthly electricity bill ($)</label>
            <input type="number" className="se-input" value={monthlyBill} onChange={e => setMonthlyBill(e.target.value)} placeholder="e.g. 180" />
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">Electricity rate ($/kWh)</label>
            <input type="number" className="se-input" value={tariff} onChange={e => setTariff(e.target.value)} step="0.001" placeholder="Leave blank to use state average" />
          </div>
          <div className="se-field">
            <label className="se-label">Usable roof area (sq ft)</label>
            <input type="number" className="se-input" value={roofArea} onChange={e => setRoofArea(e.target.value)} placeholder="e.g. 800" />
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">Primary roof direction</label>
            <select className="se-select" value={roofDir} onChange={e => setRoofDir(e.target.value)}>
              {dirOpts.map(d => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </div>
          <div className="se-field">
            <label className="se-label">Shading</label>
            <select className="se-select" value={shading} onChange={e => setShading(e.target.value)}>
              {shadingOpts.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="se-field">
          <label className="se-label">Panel type</label>
          <select className="se-select" value={panelType} onChange={e => {
            const selected = e.target.value;
            const panel = panelTypes.find(p => p.id === selected) || panelTypes[0];
            setPanelType(selected);
            // setModuleWatt(panel.defaultWatt || panel.watt || 400);
          }}>
            {panelTypes.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="se-field">
          <label className="se-label">Desired module wattage (W)</label>
          <input type="number" min="250" max="800" inputMode="numeric" className="se-input" value={moduleWatt} onChange={e => setModuleWatt(e.target.value)} placeholder="Example: 400" />
          <span className="se-hint">Auto-filled from panel type, but you can override if needed.</span>
        </div>
        {error && <div className="se-err">{error}</div>}

        <div className="se-nav-row">
          <span />
          <button className="se-btn-next" onClick={handleCalculate}>Calculate system</button>
        </div>
      </div>

      {result && (
        <div ref={resultRef} className="se-card">
          <div className="se-results-grid">
            <div className="se-res-card"><div className="se-res-label">Annual usage</div><div className="se-res-val">{result.annualUsage.toLocaleString()} kWh</div></div>
          <div className="se-res-card"><div className="se-res-label">Module wattage</div><div className="se-res-val">{result.moduleWatt} W</div></div>
          <div className="se-res-card"><div className="se-res-label">Estimated system size</div><div className="se-res-val">{result.installedKw} kW</div></div>
          <div className="se-res-card"><div className="se-res-label">Panels installed</div><div className="se-res-val">{result.installedPanels}</div></div>
          <div className="se-res-card"><div className="se-res-label">Roof capacity</div><div className="se-res-val">{result.maxPanels} panels</div></div>
          </div>
          <div className="se-results-grid">
            <div className="se-res-card"><div className="se-res-label">Annual production</div><div className="se-res-val">{result.estimatedProduction.toLocaleString()} kWh</div></div>
            <div className="se-res-card"><div className="se-res-label">Estimated system cost</div><div className="se-res-val">${result.systemCost.toLocaleString()}</div></div>
            <div className="se-res-card"><div className="se-res-label">Estimated savings</div><div className="se-res-val">${result.savings.toLocaleString()}</div></div>
            <div className="se-res-card"><div className="se-res-label">Payback</div><div className="se-res-val">{result.paybackYears ? `${result.paybackYears} yrs` : "N/A"}</div></div>
          </div>
          {result.roofWarning && (
            <div className="se-err" style={{ marginTop: 16 }}>
              Roof area may not support the recommended system size.
            </div>
          )}
          <div className="se-disclaimer">
            Estimates based on US EIA 2024 data, NREL PVWatts methodology, and the 30% federal ITC.<br />
            Actual results may vary. Consult a certified solar installer for a site-specific quote.
          </div>
        </div>
      )}
    </div>
  );
}
